import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { PRICE_OVERRIDES, FLAVOURS } from "@/data/content";

const API = process.env.REACT_APP_BACKEND_URL;
export const SHOP_URL = process.env.REACT_APP_SHOP_URL;
export const INSTAGRAM = "https://www.instagram.com/eat.swolebears?srtk=MWlvbDM1bXM3OHZqYw%3D%3D&utm_source=qr";

const ShopCtx = createContext({ products: [], singles: {}, status: "loading", shopUrl: SHOP_URL });
const FAST_FROM = Date.parse("2026-10-16T12:00:00Z");
const FAST_TO = Date.parse("2026-10-16T15:00:00Z");
const inFastWindow = () => Date.now() >= FAST_FROM && Date.now() <= FAST_TO;

export const bagsOf = (title = "") => {
  const m = title.match(/(\d+)[\s-]*Packs?/i);
  return m ? Number(m[1]) : 1;
};

export const isBuilder = (p) => p?.handle?.startsWith("build");

export const ShopProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [singles, setSingles] = useState({});
  const [status, setStatus] = useState("loading");
  const [shopUrl, setShopUrl] = useState(SHOP_URL);

  useEffect(() => {
    if (!API) { setStatus("error"); return undefined; }
    let alive = true;
    const load = () => axios.get(`${API}/api/products`)
      .then((r) => {
        if (!alive) return;
        const list = Array.isArray(r.data?.products) ? r.data.products : [];
        setProducts(list.map((p) => (PRICE_OVERRIDES[p.handle] ? { ...p, price: PRICE_OVERRIDES[p.handle] } : p)));
        setSingles(r.data?.singles && typeof r.data.singles === "object" ? r.data.singles : {});
        if (r.data?.shop_url) setShopUrl(r.data.shop_url);
        setStatus("ready");
      })
      .catch(() => alive && setStatus("error"));
    load();
    // launch morning: refresh stock every minute so open tabs pick up new inventory
    const t = setInterval(() => { if (inFastWindow()) load(); }, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const value = useMemo(() => ({ products, singles, status, shopUrl }), [products, singles, status, shopUrl]);
  return <ShopCtx.Provider value={value}>{children}</ShopCtx.Provider>;
};

export const useShop = () => useContext(ShopCtx);

// Bags left per flavour, from its single-bag product in Shopify (Infinity while loading or when untracked)
export const useFlavourStock = () => {
  const { singles, status } = useShop();
  return useMemo(() => Object.fromEntries(FLAVOURS.map((f) => {
    if (status !== "ready") return [f.key, Infinity];
    const prefix = `${f.match.replace(/\s+/g, "-")}-`;
    const entry = Object.entries(singles || {}).find(([h]) => h.startsWith(prefix));
    const n = entry ? entry[1] : 0;
    return [f.key, n === null || n === undefined ? Infinity : Number(n) || 0];
  })), [singles, status]);
};

export const useBestPerBag = () => {
  const { products } = useShop();
  const avail = products.filter((p) => p.available && p.price > 0);
  if (!avail.length) return null;
  return Math.min(...avail.map((p) => p.price / bagsOf(p.title)));
};

export const money = (n) => `$${Number(n || 0).toFixed(2)}`;
export const findSixPack = (products, f) =>
  products.find((p) => p.title.toLowerCase().includes(f.match) && bagsOf(p.title) === 6);
export const perBagMoney = (n) => `$${(Math.floor(Number(n || 0) * 100) / 100).toFixed(2)}`;
